import axios from 'axios';
import accounts from "accounts/Accounts.js"
import {sleep} from 'utils/sleep.js';
import {PUBLIC, PRIVATE, GUILD} from 'constants/save-sequence-options.js';
import {newSampleByArtist, getRelease, newRelease} from 'decentralized/artists/Artists.js';
import {exportToWAV} from 'api/exportToWav.js';
import ContentKey from 'crypto/ContentKey.js';
import {SAMPLE} from 'constants/content-types.js';
import {NOT_UPLOADED_YET} from 'constants/ipfs-constants.js';
import Item from './Item.js';
import Tone from 'tone';
import {getTempoAndOnsets} from 'utils/getTempo.js';
import ethereum from 'decentralized/ethereum.js';
import ipfsClient from 'decentralized/Ipfs.js';
import ChordSearcher from 'decentralized/chords/ChordSearcher.js';
import EncryptedIpfs from 'decentralized/guilds/EncryptedIpfs.js';
import {isNumber} from 'utils/IsNumber.js';
import {getNoteSequence} from 'tensorflow/Midifier.js';
import Colors from 'components/Colors.js';
const bufferToArrayBuffer = require('buffer-to-arraybuffer');

const colors = new Colors();
/**
 * Handles fetching of sample metadata and the actual audio
 * buffer, either from the centralized server or IPFS.
 * The plan is to eventually migrate fully to IPFS
*/
class Sample extends Item {
    constructor(id, encryptedIpfs, guildId) {
        super();
        if (typeof id == "number" || isNumber(id)) {
           this.soundId = id;
        }  else {
            this.ipfsHash = id;
        }
        this.guildId = guildId;
        this.downloadUrl = undefined;
        this.videoId = undefined;
        this.name = undefined;
        this.tags = undefined;
        this.slices = undefined;
        this.noteSequence = undefined;
        this.packs = [];
        this.isStems = false;
        this.guildId = undefined;
        if (encryptedIpfs) {
            this.encryptedIpfs = encryptedIpfs;
        } else if (window.sequencerEngine) {
            this.encryptedIpfs = new EncryptedIpfs(
                window.sequencerEngine.store,
                window.sequencerEngine.ethereum.guilds,
                window.sequencerEngine.ethereum.encryption,
                window.sequencerEngine.ethereum.crypto,
            );
        }
        
        // the actual audio buffer downloaded
        this.audioBuffer = undefined;
        
        this.addedToProject = false;
        
        this.highlightedTags = {};
        this.samplesUsed = [];
    }

    getName() {
        return this.name;
    }

    getTagsToSave() {
        if (Object.keys(this.highlightedTags).length > 0) {
            return Object.keys(this.highlightedTags).filter(x => this.highlightedTags[x]);
        }
        return this.tags;
    }

    getId() {
        return this.soundId !== undefined ? this.soundId : this.ipfsHash;
    }

    fromJson(json) {
        this.ipfsHash = json.id;
        this.name = json.name;
        this.videoId = json.videoId;
        this.tags = json.tags || [];
        this.slices = json.slices;
        this.coverArtHash = json.coverArtHash;
        this.discogsId = json.discogsId;
    }

    dispose(force=false) {
        if (!force && this.addedToProject) {
            return;
        }
        this.audioBuffer = undefined;
        this.discogsId = undefined;
        // this.name = undefined;
        this.videoId = undefined;
        this.coverArtHash = undefined;
        this.slices = undefined;
        this.packs = undefined;
    }

    isLoaded() {
        return this.audioBuffer !== undefined;
    }

    getId() {
        return this.ipfsHash !== undefined ? this.ipfsHash :
            this.soundId;
    }

    getJson() {
        return {
            id: this.getId(),
            slices: this.slices,
            name: this.name,
            videoId: this.videoId,
            tags: this.tags,
            thumbnail: this.getThumbnail(),
            discogsId: this.discogsId,
            coverArtHash: this.coverArtHash,
        };
    }

    // fetches metadata like sample name, videoId
    // and the downloadUrl or IPFS hash
    fetchMetadata() {
        if (this.ipfsHash !== undefined) {
            // get it from ethereum
            return new Promise(async (resolve, reject) =>  {
                let title = await ethereum.getTitleForSound(this.ipfsHash);
                let videoId = await ethereum.getVideoIdForSound(this.ipfsHash);
                let coverArt = await ethereum.getCoverArtForSound(this.ipfsHash);

                if (title !== undefined) {
                    this.name = title;
                }

                if (videoId !== undefined) {
                    this.videoId = videoId;
                }

                if (coverArt !== undefined) {
                    this.coverArtHash = coverArt;
                }

                resolve(this);
            });
        }
    }

    fetchTags() {
        return new Promise(async (resolve, reject) => {
            if (this.soundId === undefined &&
                this.ipfsHash !== undefined) {
                let rawTags = new Set(await ethereum.getTagsForSound(this.ipfsHash));
                this.tags = Array.from(rawTags) || [];
                resolve(this.tags || []);
                return;
            } else {
                resolve([]);
            }
        });
    }

    fetchSlices() {
        return new Promise(async (resolve, rejct) => {
            ethereum.getSlicesForSound(this.ipfsHash)
                .then(slices => {
                    this.slices = slices;
                    resolve(slices);
                });
        });
    }

    // fetches the actual audio, either via amazon s3 or
    // IPFS
    fetchAudio() {
        return new Promise((resolve, reject) => {
            if (this.ipfsHash !== undefined) { 
                this.fetchAudioFromIPFS()
                    .then(ret => resolve(ret))
                    .catch(err => {
                        reject("timed out");
                    });
            } 
        });
    }

    fetchAudioFromIPFS() {
        return new Promise((resolve, reject) => {
            let dateA = new Date().getTime();
            this.encryptedIpfs.getSound(this.ipfsHash)
                .then(async (blob) => {
                    if (blob === undefined) {
                        reject();
                        return;
                    }
                    let dateB = new Date().getTime();
                    let time = dateB - dateA;
                    try {
                        try {
                            this.audioBuffer = await Tone.Master.context.decodeAudioData(bufferToArrayBuffer(blob));
                            resolve(this);
                        } catch (e) {
                            console.log("e =", e);
                        }
                    } catch (e) {
                        reject(e);
                    }
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    async fetchGuild() {
        this.guildId = await ethereum.guildSamples.getGuildIdForSound(this.getId());
    }

    analyze(aubio, onsetThreshold=0.5) {
        if (this.tempo !== undefined) {
            return;
        }
        let {tempo, onsets} = getTempoAndOnsets(this.audioBuffer, aubio, onsetThreshold);
        this.tempo = tempo;
        this.onsets = onsets;
    }

    fetchNoteSequence() {
        return new Promise(async (resolve, reject) => {
            resolve(this.noteSequence);
        });
    }

    getThumbnail() {
        if (this.coverArtHash && this.coverArtHash !== '') {
            return ipfsClient.getHashUrl(this.coverArtHash);
        }
        return !this.videoId  || this.videoId === '' ?
            undefined : 'https://img.youtube.com/vi/' + this.videoId + '/1.jpg';
    }

    load(loadBuffer=true) {
        return new Promise(async (resolve, reject) => {
            try {
                if (loadBuffer) {
                    await this.fetchAudio();
                }
                if (window.isNFT) {
                    this.name = 'yo';
                    return resolve();
                }
                fetch('/querySample/' , {
                    method: 'POST',
                    body: JSON.stringify({
                        user: await accounts.getAccount(),
                        id: this.getId()}),
                    
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }
                })
                    .then(resp => resp.json())
                    .then(resp => {
                        this.tags = Array.from(new Set(resp.tags || []));
                        this.videoId = resp.videoId;
                        if (this.videoId === '') {
                            this.videoId = undefined;
                        }
                        this.name = resp.title;
                        this.packs = resp.packs;
                        this.favorited = resp.rating === 5;
                        this.artistName = resp.artistName;
                        this.releaseName = resp.releaseName;
                        this.sampledByUser = resp.user;
                        this.blockNumber = resp.blockNumber;
                        this.year = resp.year;
                        this.discogsId = resp.discogsId;
                        this.coverArtHash = resp.coverArtHash === "" ? undefined : resp.coverArtHash;
                        this.guildId = resp.guildId;
                        resolve();
                    })
                    .catch(err => {
                        console.log(err);
                    });
            } catch (err) {
                reject();
            }
        });
    }

    get color() {
        return colors.getColorForHash(this.getId());
    }

    getCopyWith(id, suffix) {
        let sample = new Sample(id);
        sample.videoId = this.videoId;
        sample.discogsId = this.discogsId;
        sample.coverArtHash = this.coverArtHash;
        sample.name = this.name + '(' + suffix + ')';
        sample.year = this.year;
        sample.country = this.country;
        sample.styles = this.styles;
        sample.tags = [...this.tags, suffix];
        sample.packs = [... this.packs];
        sample.isStems = true;
        return sample;
    }

    getCopy(shallowCopy=false) {
        let sample = new Sample(this.getId());
        sample.videoId = this.videoId;
        sample.discogsId = this.discogsId;
        sample.coverArtHash = this.coverArtHash;
        sample.name = this.name;
        sample.year = this.year;
        sample.country = this.country;
        sample.styles = this.styles;
        sample.tags = this.tags ? [...this.tags] : [];
        if (shallowCopy) {
            sample.audioBuffer = this.audioBuffer;
        } else if (this.audioBuffer !== undefined) {
            let copiedAudioBuffer = new AudioBuffer({
                length: this.audioBuffer.length,
                numberOfChannels: this.audioBuffer.numberOfChannels,
                sampleRate: this.audioBuffer.sampleRate
            });
            let channelData = this.audioBuffer.getChannelData(0);
            copiedAudioBuffer.copyToChannel(channelData, 0);
            if (this.audioBuffer.numberOfChannels > 1) {
                channelData = this.audioBuffer.getChannelData(1);
                copiedAudioBuffer.copyToChannel(channelData, 1);
            }
            sample.audioBuffer = copiedAudioBuffer;
        } 
        return sample;
    }

    newTag(tag) {
        this.tags.push(tag);
        if (!this.needsSave) {
            ethereum.tagSample(this.ipfsHash, tag, this.guildId);
        }
    }

    getContentType() {
        return SAMPLE;
    }

    async uploadAudioToIPFS(guildId=0) {
        if (this.getId() === NOT_UPLOADED_YET) {
            let buffer = [this.audioBuffer.getChannelData(0)];
            if (this.audioBuffer.numberOfChannels > 1) {
                buffer.push(this.audioBuffer.getChannelData(0));
            }
            let raw = await exportToWAV(buffer);
            if (guildId === 0) {
                let ipfsHash = await ipfsClient.addArrayBuffer(raw);
                this.ipfsHash = ipfsHash;
            } else {
                this.contentKey = new ContentKey();
                let ipfsHash = await this.encryptedIpfs.addSound(
                    raw, GUILD, guildId, this.contentKey);
                this.ipfsHash = ipfsHash;
                this.guildId = guildId;
            }
        }
    }
        
    async saveSample(guildId=0) {
        let contentKey = undefined;
        if (this.getId() === NOT_UPLOADED_YET) {
            let buffer = [this.audioBuffer.getChannelData(0)];
            if (this.audioBuffer.numberOfChannels > 1) {
                buffer.push(this.audioBuffer.getChannelData(0));
            }
            let raw = await exportToWAV(buffer);
            if (guildId === 0) {
                let ipfsHash = await ipfsClient.addArrayBuffer(raw);
                this.ipfsHash = ipfsHash;
            } else {
                contentKey = new ContentKey()
                let ipfsHash = await this.encryptedIpfs.addSound(
                    raw, GUILD, guildId, contentKey);
                this.ipfsHash = ipfsHash;
                this.guildId = guildId;
            }
        }
        let tags = [... this.tags];
        for (let i=tags.length; i < 4; i++) {
            tags.push('');
        }

        let styles = this.styles ? [... this.styles] : [];
        for (let i=styles.length; i < 4; i++) {
            styles.push('');
        }
            
        let obj = {
            ipfsHash: this.ipfsHash,
            title: this.name,
            videoId: this.videoId,
            tags: tags.slice(0, 4),
            styles: styles.slice(0, 4),
            country: this.country || null,
            year: this.year || 0,
            coverArt: this.coverArtHash || null,
            discogsId: this.discogsId,

        };

        this.needsSave = false;

        if (guildId === 0) {
            ethereum.newSamplesWithMetadata(
                [obj], 0);
        } else {
            ethereum.newEncryptedSamples(
                [obj], guildId, contentKey);
        }

        await sleep(1000);

        this.saveArtistInfo();
    }

    async saveArtistInfo() {
        if (this.artistName) {
            newRelease(
                this.artistName.slice(0, 32),
                this.discogsId,
                this.releaseName,
                this.coverArtHash,
                this.releaseType
            );

            await sleep(1000);

            newSampleByArtist(
                this.artistName.slice(0, 32),
                this.ipfsHash
            );
        }
    }
}

export default Sample;
